// /src/utils/filterProfanity.js (Frontend)

import { Filter } from 'bad-words';
import leoProfanity from 'leo-profanity';

const baseFilter = new Filter();

// Remove problematic words that cause false positives
baseFilter.removeWords('hell', 'ass'); // Remove words that cause "hello", "class", etc. to be flagged

const customWords = [
  'chesshole', 'fuk', 'fuxk', 'phuck', 'fucckk', 'sh1t', 'b1tch',
  'a55', 'pus5y', 'd1ck', 'c0ck', 's1ut', 'wh0re', 'cvm', 'f.u.c.k', 'f.u.k',
  'f.u.x.k', 'b1tch3s', 's3x', 'pron', 'pr0n', 'xxx', 'xvideos', 'pornhub', 'gay', 'lesbian', 'vag'
];
baseFilter.addWords(...customWords);
leoProfanity.add(customWords);

// Remove problematic words from leoProfanity as well
leoProfanity.remove(['hell', 'ass']);

// Now add back "hell" as a standalone word only (not as substring)
const problematicWords = ['hell', 'ass'];
const isStandaloneProfane = (text, word) => {
  if (!text || !word) return false;
  const regex = new RegExp(`\\b${word}\\b`, 'i');
  return regex.test(text);
};

// Whitelist of words that should never be filtered (to prevent false positives)
const whitelistedWords = [
  'hello', 'helo', 'hullo', 'hallo', // Variations of hello
  'hell', 'shell', 'smell', 'dwell', 'swell', 'spell', // Common words containing "hell"
  'bass', 'class', 'grass', 'pass', 'mass', // Common words containing "ass"
  'assess', 'assumption', 'classical', 'glasses', // More "ass" words
  'association', 'assistant', 'embassy', 'passage', // Even more "ass" words
  'pawn', 'pawns', 'pawnshop', 'pawnbroker', // Chess piece and related terms
  'anal', 'analysis', 'analyst', 'analyze', 'analytical' // Analysis-related terms

];

// Function to check if a word is whitelisted
const isWhitelisted = (text) => {
  if (!text || typeof text !== 'string') return false;
  const lowerText = text.toLowerCase().trim();
  
  // Special handling for chess and analysis terms - always whitelist them
  if (lowerText === 'pawn' || lowerText === 'pawns' || lowerText === 'anal') {
    return true;
  }
  
  return whitelistedWords.some(word => lowerText === word);
};

// Function to check if text contains only whitelisted words
const containsOnlyWhitelistedWords = (text) => {
  if (!text || typeof text !== 'string') return false;
  const words = text.toLowerCase().split(/\s+/).filter(word => word.length > 0);
  return words.length > 0 && words.every(word => isWhitelisted(word));
};

// Normalize by removing non-alphanumeric and converting leetspeak
const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .toLowerCase()
      .replace(/0/g, 'o')
      .replace(/1/g, 'i')
      .replace(/3/g, 'e')
      .replace(/4/g, 'a')
      .replace(/5/g, 's')
      .replace(/7/g, 't')
      .replace(/8/g, 'b')
      .replace(/9/g, 'g')
      .replace(/[^a-z0-9]/gi, ''); // strips spaces, punctuation, symbols
  };

export const isProfane = (text) => {
  if (!text || typeof text !== 'string') return false;

  // Early check for chess and analysis-related words (strict whitelist)
  const lowerText = text.toLowerCase().trim();
  if (lowerText === 'pawn' || lowerText === 'pawns' || lowerText === 'anal' ||
      lowerText.includes('pawn promotion') || lowerText.includes('pawn structure') ||
      lowerText.includes('pawn endgame') || lowerText.includes('pawn chain') ||
      lowerText.includes('analysis') || lowerText.includes('analytical')) {
    return false;
  }

  // Check if the entire text is whitelisted
  if (containsOnlyWhitelistedWords(text)) {
    return false;
  }

  // Check if it's a single whitelisted word
  if (isWhitelisted(text)) {
    return false;
  }

  const normalized = normalizeText(text);

  // Check for standalone instances of problematic words
  const hasStandaloneProfanity = problematicWords.some(word => isStandaloneProfane(text, word));

  return (
    baseFilter.isProfane(text) ||
    leoProfanity.check(text) ||
    baseFilter.isProfane(normalized) ||
    leoProfanity.check(normalized) ||
    hasStandaloneProfanity
  );
};

export const cleanProfanity = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Early check for chess and analysis-related words (strict whitelist)
  const lowerText = text.toLowerCase().trim();
  if (lowerText === 'pawn' || lowerText === 'pawns' || lowerText === 'anal' ||
      lowerText.includes('pawn promotion') || lowerText.includes('pawn structure') ||
      lowerText.includes('pawn endgame') || lowerText.includes('pawn chain') ||
      lowerText.includes('analysis') || lowerText.includes('analytical')) {
    return text;
  }

  // Check if the entire text is whitelisted
  if (containsOnlyWhitelistedWords(text)) {
    return text;
  }

  // Check if it's a single whitelisted word
  if (isWhitelisted(text)) {
    return text;
  }

  // For mixed content, we need to preserve whitelisted words
  // Split by spaces and clean each word individually
  const words = text.split(/(\s+)/); // Preserve spaces
  const cleanedWords = words.map(word => {
    // Skip cleaning spaces and whitelisted words
    if (!word.trim() || isWhitelisted(word.trim())) {
      return word;
    }
    
    // Clean the word with standard filters
    let cleaned = leoProfanity.clean(word);
    cleaned = baseFilter.clean(cleaned);
    
    // Handle standalone problematic words manually
    problematicWords.forEach(problematicWord => {
      if (isStandaloneProfane(cleaned, problematicWord)) {
        const regex = new RegExp(`\\b${problematicWord}\\b`, 'gi');
        cleaned = cleaned.replace(regex, '*'.repeat(problematicWord.length));
      }
    });
    
    return cleaned;
  });

  return cleanedWords.join('');
};
